import { LayoutThreeColumnShell } from 'components/authorable/layout/LayoutThreeColumn/LayoutThreeColumn';
import { LayoutTwoColumnShell } from 'components/authorable/layout/LayoutTwoColumn/LayoutTwoColumn';
import { useState } from 'react';
import { TTailwindString, classnames } from 'tailwindcss-classnames';
import Button from '../Button';
import { ButtonVariant } from '../Button/WithButton';

export interface IHasId {
  id?: string;
}

export type ShowMoreListingProps<TItem extends IHasId> = {
  items: TItem[];
  maxInitialDisplay: number;
  columns?: 1 | 2 | 3;
  seeAllCtaText: string;
  seeAllCtaVariant?: ButtonVariant;
  seeAllCtaClassName?: TTailwindString;
  parentClassName?: string;
  seeLessCtaText: string;
  /**
   * Note: The component to render must have take one of the items as a prop.
   * This type must also contain an `id` property.
   * Ideally it should inherit from ItemEx, but sometimes such with GraphQL component this isn't the case.
   */
  component: React.ComponentType<TItem>;
};

const ShowMoreListing = <TItem extends IHasId>({
  items,
  maxInitialDisplay,
  columns,
  seeAllCtaText,
  seeAllCtaVariant,
  seeAllCtaClassName,
  component,
  parentClassName,
  seeLessCtaText,
}: ShowMoreListingProps<TItem>) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const Component = component;

  const allItems = items;
  let displayeditems = [...allItems];
  let showSeeAllButton = false;

  if (items.length > maxInitialDisplay) {
    displayeditems = displayeditems.slice(0, maxInitialDisplay);
    showSeeAllButton = true;
    if (isExpanded) {
      displayeditems = [...allItems];
      showSeeAllButton = false;
    }
  }
  let list;
  if (columns === undefined || columns === 1) {
    list = (
      <ul className={parentClassName}>
        {displayeditems?.map((item) => (
          <li key={item.id} className={classnames('pb-6', 'last:pb-0')}>
            <Component {...item} />
          </li>
        ))}
      </ul>
    );
  } else {
    const groups = [];

    for (let i = 0; i < displayeditems.length; i += columns) {
      groups.push(displayeditems.slice(i, i + columns));
    }

    if (columns === 2) {
      list = (
        <div className={parentClassName}>
          {groups.map((group) => (
            <LayoutTwoColumnShell
              key={group[0].id}
              left={<Component {...group[0]} />}
              right={<Component {...group[1]} />}
            />
          ))}
        </div>
      );
    }
    if (columns === 3) {
      list = (
        <div className={parentClassName}>
          {groups.map((group) => (
            <LayoutThreeColumnShell
              key={group[0].id}
              left={<Component {...group[0]} />}
              middle={<Component {...group[1]} />}
              right={<Component {...group[2]} />}
            />
          ))}
        </div>
      );
    }
  }
  return (
    <>
      {list}
      {!isExpanded && showSeeAllButton && (
        <Button
          className={seeAllCtaClassName}
          variant={seeAllCtaVariant}
          onClick={() => setIsExpanded(true)}
          text={seeAllCtaText}
        />
      )}
      {isExpanded && (
        <Button
          className={seeAllCtaClassName}
          variant={seeAllCtaVariant}
          onClick={() => setIsExpanded(false)}
          text={seeLessCtaText}
        />
      )}
    </>
  );
};

export default ShowMoreListing;
